import {
  Box,
  CheckerBoard,
  Image,
  MediaCardPlaceholder,
  ProgressBar,
  type ProgressBarProps
} from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import type { MediaAsset } from "../../../hooks/mediaLibraryData"
import { useSelectedMedia } from "../../../hooks/useSelectedMedia"
import { useMediaLibrary } from "../../MediaLibraryContext"

export function SidepaneImage() {
  const selectedMedia = useSelectedMedia()
  const { uploadingFiles } = useMediaLibrary()
  const [firstMedia] = selectedMedia

  if (!firstMedia) return null

  if (selectedMedia.length === 2) {
    return (
      <ImageWrapper>
        {selectedMedia.map((media, index) => (
          <div
            key={media.id}
            style={{
              position: "absolute",
              transform:
                index === 0
                  ? "translateX(25%) rotate(15deg) "
                  : "translateX(-25%) rotate(-15deg) "
            }}
          >
            <FixedSizedImage media={media} />
          </div>
        ))}
      </ImageWrapper>
    )
  }

  if (selectedMedia.length > 2) {
    return (
      <ImageWrapper>
        {
          // Show only the last 4 selected images
          selectedMedia.slice(-4).map((media, index) => (
            // <div /> is used as dynamic padding size doesn't comply with our theme tokens
            <div
              key={media.id}
              style={{
                position: "absolute",
                paddingRight: index * 16
              }}
            >
              <FixedSizedImage media={media} />
            </div>
          ))
        }
      </ImageWrapper>
    )
  }

  const uploadStatus = uploadingFiles.find(
    (file) => file.id === firstMedia.id
  )?.status

  // when receiving the media from the server the kind will be the media extension e.g. "png"
  // while uploading the media kind will show as media type and extension e.g. "image/png"
  const isImage = firstMedia.kind.startsWith("image")

  return (
    <Box flexDirection="column">
      <CheckerBoard>
        <ImageWrapper uploadStatus={uploadStatus} hasImage={isImage}>
          {isImage ? (
            <Image src={firstMedia.url} alt={firstMedia.alt} sizing="contain" />
          ) : (
            <MediaCardPlaceholder
              kind={firstMedia.kind}
              filename={firstMedia.filename}
            />
          )}
        </ImageWrapper>
      </CheckerBoard>
    </Box>
  )
}

interface ImageWrapperProps {
  children: ReactNode
  uploadStatus?: UploadProgressStatus
  hasImage?: boolean
}

function ImageWrapper(props: ImageWrapperProps) {
  const { children, uploadStatus, hasImage } = props

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      position="relative"
      height={200}
      width="100%"
      backgroundColor={hasImage ? "transparent" : "purple2"}
    >
      {children}
      {uploadStatus && <UploadStatus uploadStatus={uploadStatus} />}
    </Box>
  )
}

interface UploadStatusProps {
  uploadStatus: UploadProgressStatus
}

function UploadStatus(props: UploadStatusProps) {
  const { uploadStatus } = props
  const { value, color, height, transition } =
    getProgressPropsFromUploadStatus(uploadStatus)

  return (
    <Box
      position="absolute"
      backgroundColor="greyAlpha1"
      width="100%"
      height="100%"
      padding={{ inline: 16 }}
      justifyContent="end"
      flexDirection="column"
    >
      <ProgressBar
        value={value}
        color={color}
        height={height}
        transition={transition}
      />
    </Box>
  )
}

type UploadProgressStatus = "idle" | "uploading" | "success" | "error"

/**
 * Matches upload status to ProgressBar props
 */
function getProgressPropsFromUploadStatus(
  status: UploadProgressStatus
): ProgressBarProps {
  switch (status) {
    case "idle":
      return {
        value: 0,
        height: "collapsed",
        color: "grey4"
      }
    case "uploading":
      return {
        value: 90,
        transition: "slow",
        color: "purple9"
      }
    case "success":
      return {
        value: 100,
        height: "collapsed",
        color: "purple9"
      }
    case "error":
      return {
        value: 100,
        color: "tomato11"
      }
  }
}

interface FixedSizedImageProps {
  media: MediaAsset
}

function FixedSizedImage(props: FixedSizedImageProps) {
  const { media } = props

  return (
    <Box width={111} height={152}>
      {media.kind === "image" ? (
        <Image
          borderRadius={8}
          src={media.url}
          alt={media.alt}
          sizing="cover"
        />
      ) : (
        <MediaCardPlaceholder kind={media.kind} filename={media.filename} />
      )}
    </Box>
  )
}
